import { observer } from 'mobx-react';

// Contexts
import ItemSheetProvider from 'contexts/ItemSheet';

// Hooks
import { type ReactNode, useEffect, useRef } from 'react';
import useEditor from 'hooks/use-editor';
import { useHistory, useParams } from 'react-router-dom';
import useQuery from 'hooks/use-query';

// Models
import CartItem from 'models/CartItem';
import SharedCartItem from 'models/SharedCartItem';
import { Keys } from 'constants/keyboard';

function CartItemModalContainer({
  getCartItemFromQueryParams,
  children,
}: {
  getCartItemFromQueryParams: () => CartItem | SharedCartItem | undefined;
  children: ReactNode;
}) {
  const { getCartItem, removeCartItem } = useEditor();
  const history = useHistory();
  const { locationCode } = useParams<{ locationCode: string }>();
  const query = useQuery();
  const prevCartItem = useRef<CartItem | SharedCartItem | null>(null);

  const fromEditor = getCartItem();
  const fromQueryParams = getCartItemFromQueryParams();
  const cartItem = prevCartItem.current ?? fromEditor ?? fromQueryParams;

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === Keys.Esc || e.key === Keys.Escape) {
        history.goBack();
      }
    };

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      removeCartItem();
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [history, removeCartItem]);

  if (!cartItem) {
    history.push({ pathname: `/${locationCode}`, search: query.toString() });
    return null;
  }

  prevCartItem.current = cartItem;

  return <ItemSheetProvider cartItem={cartItem}>{children}</ItemSheetProvider>;
}

export default observer(CartItemModalContainer);
